import React, { useEffect, useMemo, useRef, useState } from "react";
import "./css/MessageCrafter.css";

const MESSAGE_LIMIT = 480;
const SENTENCE_PATTERN = /[^.!?\n]+[.!?]+["')\]]*|[^.!?\n]+/g;

function normalizeSegment(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function splitIntoSentenceUnits(text) {
  const normalized = String(text || "").replace(/\r\n?/g, "\n").trim();
  if (!normalized) return [];

  const units = [];
  normalized
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .forEach((paragraph, paragraphIndex) => {
      const sentenceMatches = paragraph.match(SENTENCE_PATTERN) || [paragraph];
      sentenceMatches
        .map(normalizeSegment)
        .filter(Boolean)
        .forEach((sentence, sentenceIndex) => {
          units.push({
            text: sentence,
            joiner: paragraphIndex > 0 && sentenceIndex === 0 ? "\n\n" : " ",
          });
        });
    });

  return units;
}

function splitOversizedSegment(segment, limit = MESSAGE_LIMIT) {
  const words = normalizeSegment(segment).split(" ").filter(Boolean);
  const chunks = [];
  let current = "";

  words.forEach((word) => {
    if (word.length > limit) {
      if (current) {
        chunks.push(current);
        current = "";
      }

      for (let index = 0; index < word.length; index += limit) {
        chunks.push(word.slice(index, index + limit));
      }
      return;
    }

    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= limit) {
      current = candidate;
      return;
    }

    if (current) chunks.push(current);
    current = word;
  });

  if (current) chunks.push(current);
  return chunks;
}

function appendUnit(current, unit) {
  if (!current) return unit.text;
  return `${current}${unit.joiner || " "}${unit.text}`;
}

function chunkMessage(text, limit = MESSAGE_LIMIT) {
  const units = splitIntoSentenceUnits(text);
  const chunks = [];
  let current = "";

  units.forEach((unit) => {
    if (unit.text.length > limit) {
      if (current) {
        chunks.push(current);
        current = "";
      }
      chunks.push(...splitOversizedSegment(unit.text, limit));
      return;
    }

    const candidate = appendUnit(current, unit);
    if (candidate.length <= limit) {
      current = candidate;
      return;
    }

    if (current) chunks.push(current);
    current = unit.text;
  });

  if (current) chunks.push(current);
  return chunks;
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch (error) {
      // Fall through to the textarea copy path for browsers that block clipboard writes.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-1000px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function getChunkRows(chunk) {
  const lineCount = chunk.split("\n").length;
  const wrappedLineEstimate = Math.ceil(chunk.length / 72);
  return Math.min(10, Math.max(3, lineCount + wrappedLineEstimate - 1));
}

const MessageCrafter = () => {
  const [draft, setDraft] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);
  const copyTimerRef = useRef(null);

  const chunks = useMemo(() => chunkMessage(draft), [draft]);
  const longestChunkLength = chunks.reduce((max, chunk) => Math.max(max, chunk.length), 0);

  useEffect(() => () => {
    if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
  }, []);

  const markCopied = (index) => {
    setCopiedIndex(index);
    if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => setCopiedIndex(null), 1500);
  };

  const handleCopyChunk = async (chunk, index) => {
    await copyText(chunk);
    markCopied(index);
  };

  const handleCopyAll = async () => {
    await copyText(chunks.join("\n\n"));
    markCopied("all");
  };

  return (
    <main className="message-crafter-page">
      <header className="message-crafter-header">
        <div>
          <p className="message-crafter-kicker">Tools / Message Crafter</p>
          <h1>Message Crafter</h1>
        </div>
        <div className="message-crafter-stats" aria-label="Message limits">
          <div className="message-crafter-stat">
            <span>Limit</span>
            <strong>{MESSAGE_LIMIT}</strong>
          </div>
          <div className="message-crafter-stat">
            <span>Chunks</span>
            <strong>{chunks.length || 0}</strong>
          </div>
          <div className="message-crafter-stat">
            <span>Longest</span>
            <strong>{longestChunkLength}</strong>
          </div>
        </div>
      </header>

      <div className="message-crafter-layout">
        <section className="message-crafter-panel">
          <div className="message-crafter-panel-head">
            <h2>Draft</h2>
            <span>{draft.length.toLocaleString()} characters</span>
          </div>
          <textarea
            className="message-crafter-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Write or paste your message here."
            spellCheck={true}
          />
        </section>

        <section className="message-crafter-panel">
          <div className="message-crafter-panel-head">
            <h2>Messages</h2>
            <div className="message-crafter-actions">
              {chunks.length > 0 && (
                <button type="button" onClick={handleCopyAll}>
                  {copiedIndex === "all" ? "Copied" : "Copy All"}
                </button>
              )}
              {draft && (
                <button type="button" onClick={() => setDraft("")}>
                  Clear
                </button>
              )}
            </div>
          </div>

          {chunks.length > 0 ? (
            <div className="message-crafter-chunks">
              {chunks.map((chunk, index) => (
                <article className="message-crafter-chunk" key={`${index}-${chunk.length}`}>
                  <div className="message-crafter-chunk-head">
                    <strong>Message {index + 1}</strong>
                    <div className="message-crafter-actions">
                      <span>{chunk.length}/{MESSAGE_LIMIT}</span>
                      <button
                        type="button"
                        onClick={() => handleCopyChunk(chunk, index)}
                      >
                        {copiedIndex === index ? "Copied" : "Copy"}
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="message-crafter-output"
                    value={chunk}
                    readOnly
                    rows={getChunkRows(chunk)}
                  />
                </article>
              ))}
            </div>
          ) : (
            <div className="message-crafter-empty">Chunks will appear as you type.</div>
          )}
        </section>
      </div>
    </main>
  );
};

export default MessageCrafter;
